import { ReportAssumption, ReportPaymentType } from '@futrli-sdk/predict';

import { updateAssumption } from '../../../../../core/api/assumption';
import { changePaymentType } from '../../../../../core/api/payment-type';
import { ASSUMPTION_TYPES, PAYMENT_TYPES } from '../../../../../core/constants';
import { IPredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { IDetailContext } from '../../../context/detail-context/detail-context';
import { AllowedAssumptionTypes, AllowedPaymentTypes } from '../../actions/actions';

export interface PendingDataState {
  prediction: Partial<AllowedAssumptionTypes> | undefined;
  paymentType: AllowedPaymentTypes | undefined;
  paymentTypeType: PAYMENT_TYPES;
}

/**
 * Given our two primary row times (payment type and prediction) store information
 * in fields with the same names (but different types) it's required to pull various
 * id and type info from different fields. This checks the given row and pulls out
 * the info from the right places.
 *
 * @param {ReportAssumption | ReportPaymentType} row
 * @returns {[ASSUMPTION_TYPES, string, PAYMENT_TYPES, string]}
 */
export const extractIdsAndTypes = (
  row: ReportAssumption | ReportPaymentType
): [ASSUMPTION_TYPES, string, PAYMENT_TYPES, string] => {
  return [
    ((<ReportPaymentType>row).assumption_type ?? row.type) as ASSUMPTION_TYPES,
    (<ReportPaymentType>row).assumption_id ?? row.index,
    ((<ReportAssumption>row).paymenttype_type ?? row.type) as PAYMENT_TYPES,
    (<ReportAssumption>row).paymenttype_id ?? row.index
  ];
};

/**
 * Abstracted function to send updates for given prediction or payment type data.
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} forecastId
 * @param {ASSUMPTION_TYPES} predictionType
 * @param {string} predictionId
 * @param {PAYMENT_TYPES} paymentType
 * @param {PendingDataState} data
 * @param {Function} onSuccess
 * @param {Function} onFailure
 * @param {Function} fetchForecast
 * @param {Function} fetchForecastReport
 * @param {Function} addErrorMessage
 */
export const updatePredictionAndPaymentType = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  predictionType: ASSUMPTION_TYPES,
  predictionId: string,
  paymentType: PAYMENT_TYPES,
  data: Partial<PendingDataState>,
  fetchForecastReport: IDetailContext['fetchForecastReport'],
  addErrorMessage: IPredictManagementContext['addErrorMessage'],
  fetchForecast?: IDetailContext['fetchForecast'],
  onSuccess?: () => void,
  onFailure?: () => void
) => {
  try {
    const updates = [];
    if (data.paymentType) {
      updates.push(
        changePaymentType(workspaceId, organisationId, forecastId, predictionId, paymentType, data.paymentType)
      );
    }

    if (data.prediction) {
      updates.push(
        updateAssumption(data.prediction, workspaceId, organisationId, forecastId, predictionType, predictionId)
      );
    }

    await Promise.all(updates);

    onSuccess?.();

    fetchForecastReport('all');
    fetchForecast?.();
  } catch {
    addErrorMessage(new Error('Failed to update payment type or prediction'));
    onFailure?.();
  }
};
